<template>
  <div
    class="comment-container"
    :data-testid="props.testIdBase ? `${props.testIdBase}-container` : undefined"
  >
    <div
      class="comment-wrapper"
      :data-testid="props.testIdBase ? `${props.testIdBase}-wrapper` : undefined"
      :data-comment-type="type"
      :data-comment-editable="editable ? 'true' : 'false'"
      :data-comment-editing="isEditing ? 'true' : 'false'"
      :data-comment-dirty="isDirty ? 'true' : 'false'"
      :data-comment-draft="isDraft ? 'true' : 'false'"
      :class="[
        type === 'me' ? 'align-right' : type === 'other' ? 'align-left' : 'align-center',
        type === 'note' ? 'note-style' : 'bubble-style',
        isDraft ? 'draft' : '',
        isEditing ? 'editing-full' : '',
        showEditor ? 'is-editing' : '',
      ]"
      @click="openEditor"
      @focus="openEditor"
      @blur="handleBlur"
    >
      <div
        class="bubble-header"
        :data-testid="props.testIdBase ? `${props.testIdBase}-header` : undefined"
      >
        <slot
          name="header"
          :isDirty="isDirty"
          :editable="!!editable"
          :isEditing="isEditing"
          :text="localText"
          :initialText="initialText"
        />
      </div>

      <div
        v-if="!isEditing"
        class="text-content"
        :data-testid="props.testIdBase ? `${props.testIdBase}-text` : undefined"
      >
        <slot>
          {{ getTextForLocale(modelValue) }}
        </slot>
      </div>

      <div
        v-else
        :data-testid="props.testIdBase ? `${props.testIdBase}-editor` : undefined"
        :class="props.editableOnFocus && !isEditing ? 'cursor-pointer' : ''"
      >
        <SimpleTextArea
          v-model="localText"
          :testId="props.testIdBase ? `${props.testIdBase}-input` : undefined"
          name="noteText"
          class="text-input"
          :label="`${type === 'note' ? 'noteText' : 'commentText'}`"
          :placeholder="(props.placeholder ?? type === 'note') ? 'note' : 'comment'"
          multiLanguage
          :autoResize="true"
          :rows="10"
        />
      </div>

      <div
        v-if="editable && isDirty"
        class="actions gap-2 py-4"
        :data-testid="props.testIdBase ? `${props.testIdBase}-actions` : undefined"
      >
        <Button
          v-tooltip="{ value: t('titletags.reset'), showDelay: 300 }"
          icon="pi pi-undo"
          class="!p-2 !h-8 !w-8"
          :data-testid="props.testIdBase ? `${props.testIdBase}-reset` : undefined"
          :aria-label="t('titletags.reset')"
          outlined
          @click="handleReset"
        />
        <Button
          v-if="localText.length"
          v-tooltip="{ value: t('titletags.save'), showDelay: 300 }"
          icon="pi pi-check"
          class="!p-2 !h-8 !w-8"
          :data-testid="props.testIdBase ? `${props.testIdBase}-submit` : undefined"
          :aria-label="t('buttons.save')"
          :disabled="!profileStore.userProfile?.isSeyboldUser"
          @click="emitSubmit"
        />
      </div>
    </div>

    <span
      v-if="type === 'other'"
      class="userName"
      :data-testid="props.testIdBase ? `${props.testIdBase}-username` : undefined"
    >
      <i
        class="pi pi-user comment-icon"
        aria-hidden="true"
        :data-testid="props.testIdBase ? `${props.testIdBase}-usericon` : undefined"
      ></i>
      {{ userName }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useMultilingualText } from '@/composables/useMultilingualText';
import { useProfileStore } from '@/stores/profile';

import SimpleTextArea from '../simple-inputs/SimpleTextArea.vue';

const props = defineProps<{
  modelValue: string | undefined;
  type: 'me' | 'other' | 'note';
  isReply?: boolean;
  editable?: boolean;
  editableOnFocus?: boolean;
  userName?: string;
  draft?: boolean;
  placeholder?: string;
  commitKey?: string | number;
  externalDirty?: boolean;

  /** E2E helpers */
  testIdBase?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'submit', payload: { text: string; isNote: boolean; isReply: boolean }): void;
  (e: 'blur'): void;
  (e: 'reset'): void;
}>();

const { t } = useI18n();
const profileStore = useProfileStore();
const { getTextForLocale } = useMultilingualText();
const isDraft = computed(() => !!props.draft);
const editable = computed(() => !!props.editable);

const localText = computed<string>({
  get: () => props.modelValue ?? '',
  set: (v) => emit('update:modelValue', v),
});

const initialText = ref<string>((props.modelValue ?? '').trim());
const textIsDirty = computed(() => (localText.value ?? '').trim() !== initialText.value);
const isDirty = computed(() => textIsDirty.value || !!props.externalDirty);
const isEmpty = computed(() => (localText.value ?? '').trim().length === 0);

// editing state: start closed if editableOnFocus, otherwise open when editable is true
const isEditing = ref<boolean>(
  isEmpty.value || (!(props.editableOnFocus ?? true) && editable.value),
);

// show actions only when dirty
const showEditor = computed(() => editable.value && (isEditing.value || isEmpty.value));

function openEditor() {
  if (!editable.value || isEditing.value) return;
  isEditing.value = true;
}

function handleBlur() {
  isEditing.value = isEmpty.value ? true : false;
  emit('blur');
}

function emitSubmit() {
  emit('submit', {
    text: localText.value,
    isNote: props.type === 'note',
    isReply: !!props.isReply,
  });
  initialText.value = (localText.value ?? '').trim();
  isEditing.value = false;
}

function handleReset() {
  localText.value = initialText.value;
  isEditing.value = isEmpty.value ? true : false;
  emit('reset');
}

watch(localText, (val) => {
  emit('update:modelValue', val);
});

watch(
  () => props.commitKey,
  () => {
    initialText.value = (props.modelValue ?? '').trim();
    isEditing.value = isEmpty.value ? true : false;
  },
);

watch(
  () => props.modelValue,
  (v) => {
    if (!textIsDirty.value) {
      initialText.value = (v ?? '').trim();
    }
  },
);
</script>

<style scoped lang="css">
@layer app {
  .comment-container {
    display: flex;
    flex-direction: column;
  }

  .comment-wrapper {
    max-width: 80%;
    margin: 1rem;
    padding: 1rem;
    position: relative;
    font-size: 0.95rem;
    line-height: 1.5;
    word-wrap: break-word;
  }

  :deep(.text-input) {
    background-color: transparent !important;
    border: none !important;
  }

  .comment-wrapper.is-editing,
  .comment-wrapper.editing-full {
    min-width: 80%;
    box-sizing: border-box;
  }

  /* Alignment wrappers */
  .align-left {
    align-self: flex-start;
  }

  .align-right {
    align-self: flex-end;
  }

  .align-center {
    align-self: center;
  }

  /* Bubbles (comment style) */
  .bubble-style {
    border-radius: 1rem;
    position: relative;
  }

  .align-left.bubble-style {
    background-color: #e0f8e9;
    border-bottom-left-radius: 0;
  }
  .align-left.bubble-style::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: -10px;
    width: 0;
    height: 0;
    border: 10px solid transparent;
    border-right-color: #e0f8e9;
    border-left: 0;
    border-bottom: 0;
    margin-bottom: -1px;
  }

  .align-right.bubble-style {
    background-color: #d0e9ff;
    border-bottom-right-radius: 0;
  }
  .align-right.bubble-style::after {
    content: '';
    position: absolute;
    bottom: 0;
    right: -10px;
    width: 0;
    height: 0;
    border: 10px solid transparent;
    border-left-color: #d0e9ff;
    border-right: 0;
    border-bottom: 0;
    margin-bottom: -1px;
  }

  /* Notiz-Stil */
  .note-style {
    background-color: #fff8c6;
    color: #4a4a00;
    border: 1px solid #f2e08c;
    box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    position: relative;
  }
  .note-style::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 24px;
    height: 24px;
    background: #f2e08c;
    clip-path: polygon(100% 0, 0 0, 100% 100%);
  }

  .text-input {
    width: 100%;
    border: 1px solid #ccc;
    border-radius: 0.5rem;
    padding: 0.5rem;
    resize: vertical;
    font-family: inherit;
    font-size: 0.95rem;
    background: #fff;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.5rem;
  }

  .text-content {
    white-space: pre-wrap;
    padding: 0.5rem;
  }

  /* Draft variant */
  .draft.bubble-style {
    background-color: #ffffff;
    border: 2px dashed #d1d5db;
  }

  .draft.align-left.bubble-style::after,
  .draft.align-right.bubble-style::after {
    border-left-color: transparent !important;
    border-right-color: transparent !important;
  }

  .draft.note-style {
    /* background-color: #ffffff; */
    background-color: rgba(242, 224, 140, 0.25);
    border: 2px dashed #f2e08c;
    box-shadow: none;
  }
  /* .draft.note-style::before { display: none; } */
}
</style>
